import { Formik, Form } from "formik";
import PrimaryModal from "../UI/Modal/PrimaryModal";
import { FormikSelect, FormikInput, PrimaryButton, SecondaryButton } from "../UI";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useUserContext } from "../../contexts/UserContext";
import { createOrgPlanMapping, updateOrgPlanMapping } from "../../utils/APIs";
import { orgPlanMappingSchema } from "../../schemas";
import { useContentfulContext } from "../../contexts/ContentfulContext";

const OrgPlanMappingModal = ({ setSelectedAction, selectedMapping, getMappings, plans, organizations }) => {
    const [submitError, setSubmitError] = useState("");
    const { content } = useContentfulContext()
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { userData } = useUserContext();
    const isEdit = Boolean(selectedMapping);

    const initialValues = {
        org_id: isEdit
            ? (organizations.find(o => o.name === selectedMapping?.org_slug)?.org_token || "")
            : "",
        plan_id: selectedMapping?.plan_id?._id || "",
        start_date: selectedMapping?.start_date ?
            new Date(selectedMapping.start_date).toISOString().split('T')[0]
            :
            new Date().toISOString().split('T')[0],
        end_date: selectedMapping?.end_date ?
            new Date(selectedMapping.end_date).toISOString().split('T')[0]
            :
            "",
        // is_active: selectedMapping?.is_active || false,
    };

    useEffect(() => {
        if (isEdit && selectedMapping?.plan_id) {
            const existingPlan = plans.find(plan => plan._id === selectedMapping.plan_id._id);
            setSelectedPlan(existingPlan);
        }
    }, [isEdit, selectedMapping, plans]);

    const addMapping = async (payload, actions) => {
        try {
            const result = await createOrgPlanMapping(userData?.token, userData.selectedOrganization.org_token, payload);
            if (result.status) {
                toast.success(result.message);
                getMappings();
                actions.resetForm();
                setSubmitError("");
                setSelectedAction("");
            }
        } catch (error) {
            const errorMessage = error?.response?.data.message || "Failed to create the mapping. Please try again.";
            setSubmitError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const editMapping = async (payload, actions) => {
        try {
            const result = await updateOrgPlanMapping(userData?.token, userData.selectedOrganization.org_token, selectedMapping._id, payload);
            if (result.status) {
                toast.success(result.message);
                getMappings();
                actions.resetForm();
                setSubmitError("");
                setSelectedAction("");
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message || "Failed to update the mapping. Please try again.";
            setSubmitError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (values, actions) => {
        setIsSubmitting(true);
        const payload = {
            ...values,
            start_date: new Date(values.start_date),
        };
        if (selectedPlan?.org_level !== "1") {
            payload.end_date = new Date(values.end_date);
        } else {
            delete payload.end_date
        }

        if (isEdit) {
            editMapping(payload, actions);
        } else {
            addMapping(payload, actions);
        }
    };

    const handlePlanChange = (e, setFieldValue) => {
        const selectedPlanId = e?.target ? e.target.value : e;
        setFieldValue("plan_id", selectedPlanId);

        const selectedPlan = plans.find(plan => plan._id === selectedPlanId);
        setSelectedPlan(selectedPlan);

        const today = new Date().toISOString().split('T')[0];
        setFieldValue("start_date", today);

        if (selectedPlan) {
            const durationInDays = selectedPlan.duration;
            const newEndDate = new Date();
            newEndDate.setDate(newEndDate.getDate() + durationInDays);

            setFieldValue("end_date", newEndDate.toISOString().split('T')[0]);
        } else {
            setFieldValue("end_date", "");
        }
    };

    return (
        <PrimaryModal title={isEdit ? "Edit Mapping" : "Create New Mapping"} hideModal={() => setSelectedAction("")}>
            <Formik
                initialValues={initialValues}
                validationSchema={orgPlanMappingSchema}
                onSubmit={handleSubmit}
                validateOnBlur
                validateOnChange
            >
                {({ setFieldValue }) => (
                    <Form className="space-y-4 p-6">
                        {/* Plan ID Field - Dropdown */}
                        <FormikSelect
                            id="plan_id"
                            name="plan_id"
                            label={content?.ADMIN_ORG_PLAN_MAPPING?.modal?.plan_id_label}
                            placeholder={content?.ADMIN_ORG_PLAN_MAPPING?.modal?.select_plan}
                            options={plans?.map((plan) => ({
                                value: plan._id,
                                label: plan.plan_name
                            })) || []}
                            onChange={(e) => handlePlanChange(e, setFieldValue)}
                        />

                        {/* Organization ID Field */}
                        <FormikSelect
                            id="org_id"
                            name="org_id"
                            label={content?.ADMIN_ORG_PLAN_MAPPING?.modal?.org_id_label}
                            placeholder={content?.ADMIN_ORG_PLAN_MAPPING?.modal?.select_org}
                            options={organizations?.map((org) => ({
                                value: org.org_token,
                                label: org.display_name
                            })) || []}
                        />

                        {/* Start Date Field */}
                        <FormikInput
                            type="date"
                            id="start_date"
                            name="start_date"
                            disabled={true}
                            label={content?.ADMIN_ORG_PLAN_MAPPING?.modal?.start_date_label}
                            min={!isEdit ? new Date().toISOString().split("T")[0] : undefined}
                        />

                        {/* End Date Field */}
                        {selectedPlan?.org_level !== "1" && (
                            <FormikInput
                                type="date"
                                id="end_date"
                                name="end_date"
                                disabled={true}
                                label={content?.ADMIN_ORG_PLAN_MAPPING?.modal?.end_date_label}
                            />
                        )}

                        {/* Submit Error */}
                        {submitError && <div className="text-red-500 text-sm mb-4">{submitError}</div>}

                        {/* Form Actions */}
                        <div className="flex justify-end space-x-4">
                            <SecondaryButton type="button" handleClick={() => setSelectedAction("")}>
                                {content?.ADMIN_ORG_PLAN_MAPPING?.modal?.cancel}
                            </SecondaryButton>
                            <PrimaryButton type="submit" disabled={isSubmitting}>
                                {isSubmitting ?
                                    "Submitting..."
                                    :
                                    isEdit ?
                                        content?.ADMIN_ORG_PLAN_MAPPING?.modal?.update_mapping
                                        :
                                        content?.ADMIN_ORG_PLAN_MAPPING?.modal?.create_mapping
                                }
                            </PrimaryButton>
                        </div>
                    </Form>
                )}
            </Formik>
        </PrimaryModal>
    );
};

export default OrgPlanMappingModal;